/**
 * Contacts store - manages organization contact directory state
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import {
  contactsApi,
  type OrganizationContact,
  type CreateContactRequest,
  type UpdateContactRequest,
} from '@services/api/contacts';

interface ContactsState {
  // State
  contacts: OrganizationContact[];
  searchResults: OrganizationContact[];
  isLoading: boolean;
  isSearching: boolean;
  error: string | null;
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;

  // Actions
  fetchContacts: (organizationId: string, page?: number) => Promise<void>;
  searchContacts: (
    organizationId: string,
    query: string
  ) => Promise<OrganizationContact[]>;
  createContact: (data: CreateContactRequest) => Promise<OrganizationContact>;
  updateContact: (
    contactId: string,
    data: UpdateContactRequest
  ) => Promise<OrganizationContact>;
  deleteContact: (contactId: string) => Promise<void>;
  clearSearchResults: () => void;
  clearError: () => void;
  reset: () => void;
}

// Monotonic sequence guard so stale autocomplete responses can't clobber newer ones
let searchSeq = 0;

const initialState = {
  contacts: [] as OrganizationContact[],
  searchResults: [] as OrganizationContact[],
  isLoading: false,
  isSearching: false,
  error: null as string | null,
  total: 0,
  page: 1,
  pageSize: 20,
  hasMore: false,
};

export const useContactsStore = create<ContactsState>()(
  immer((set, get) => ({
    ...initialState,

    fetchContacts: async (organizationId: string, page = 1) => {
      set((state) => {
        state.isLoading = true;
        state.error = null;
      });

      try {
        const { pageSize } = get();
        const response = await contactsApi.list({
          organizationId,
          page,
          pageSize,
        });

        set((state) => {
          state.contacts = response.items;
          state.total = response.total;
          state.page = response.page;
          state.hasMore = response.hasMore;
          state.isLoading = false;
        });
      } catch (error) {
        set((state) => {
          state.error =
            error instanceof Error ? error.message : 'Failed to fetch contacts';
          state.isLoading = false;
        });
      }
    },

    searchContacts: async (organizationId: string, query: string) => {
      const seq = ++searchSeq;
      set((state) => {
        state.isSearching = true;
        state.error = null;
      });

      try {
        const response = await contactsApi.list({
          organizationId,
          search: query,
          pageSize: 10, // Limit for autocomplete
        });

        // Ignore responses superseded by a newer search
        if (seq !== searchSeq) {
          return response.items;
        }

        set((state) => {
          state.searchResults = response.items;
          state.isSearching = false;
        });

        return response.items;
      } catch (error) {
        // Only surface the error if this is still the latest search
        if (seq === searchSeq) {
          set((state) => {
            state.error =
              error instanceof Error
                ? error.message
                : 'Failed to search contacts';
            state.isSearching = false;
          });
        }
        return [];
      }
    },

    createContact: async (data: CreateContactRequest) => {
      set((state) => {
        state.isLoading = true;
        state.error = null;
      });

      try {
        const contact = await contactsApi.create(data);
        set((state) => {
          state.contacts.unshift(contact);
          state.total += 1;
          state.isLoading = false;
        });
        return contact;
      } catch (error) {
        set((state) => {
          state.error =
            error instanceof Error ? error.message : 'Failed to create contact';
          state.isLoading = false;
        });
        throw error;
      }
    },

    updateContact: async (contactId: string, data: UpdateContactRequest) => {
      try {
        const updated = await contactsApi.update(contactId, data);
        set((state) => {
          const index = state.contacts.findIndex((c) => c.id === contactId);
          if (index !== -1) {
            state.contacts[index] = updated;
          }
          // Also update in search results if present
          const searchIndex = state.searchResults.findIndex(
            (c) => c.id === contactId
          );
          if (searchIndex !== -1) {
            state.searchResults[searchIndex] = updated;
          }
        });
        return updated;
      } catch (error) {
        set((state) => {
          state.error =
            error instanceof Error ? error.message : 'Failed to update contact';
        });
        throw error;
      }
    },

    deleteContact: async (contactId: string) => {
      try {
        await contactsApi.delete(contactId);
        set((state) => {
          state.contacts = state.contacts.filter((c) => c.id !== contactId);
          state.searchResults = state.searchResults.filter(
            (c) => c.id !== contactId
          );
          state.total -= 1;
        });
      } catch (error) {
        set((state) => {
          state.error =
            error instanceof Error ? error.message : 'Failed to delete contact';
        });
        throw error;
      }
    },

    clearSearchResults: () => {
      set((state) => {
        state.searchResults = [];
      });
    },

    clearError: () => {
      set((state) => {
        state.error = null;
      });
    },

    reset: () => {
      set(initialState);
    },
  }))
);
